/**
 * Контракт служебной сборки отчёта.
 *
 * Страница принадлежит специалисту сервиса, а не клиенту: она задаёт, из
 * чего собрать документ, и запускает сборку. Точки входа из кабинета у неё
 * нет и не планируется — ссылку открывает тот, кто знает адрес.
 *
 * Отсюда тон формы: здесь не объясняют, что такое инцидент, здесь называют
 * правило, по которому он считается. Подписи короткие, единицы измерения
 * проставлены везде — читатель знает предмет и ищет поле, а не смысл.
 *
 * @format
 */

import type {SectionId} from '../ExecutiveReport/types';

/** Кому адресован собираемый документ — от этого зависит набор этажей. */
export type ReportAudience = 'advertiser' | 'agency';

/** Что делает сборка прямо сейчас. Правит и плашку сверху, и кнопку внизу. */
export type ReportOrderState = 'idle' | 'building' | 'done' | 'failed';

/**
 * Проект в выпадающем списке.
 *
 * Проект выбирается, а не набирается: имена кабинетов длинные, похожие и с
 * кавычками, и набранное руками название с опечаткой означает сборку не по
 * тому кабинету. Наружу уходит идентификатор — по нему сборка и ищет данные.
 */
export interface CompanyOption {
  id: string;
  name: string;
}

/**
 * Проекты, доступные специалисту. В хост-приложении список приходит с
 * сервера; здесь лежит демонстрационный, чтобы страница работала в
 * Storybook и в Job'е без подключения.
 */
export const COMPANY_OPTIONS: CompanyOption[] = [
  {id: 'olha', name: 'Мебельная фабрика «Ольха»'},
  {id: 'test-account-1', name: 'test account1'},
  {id: 'gruzovichok', name: 'Грузовичок 24'},
  {id: 'dentalux', name: 'Стоматология «Денталюкс»'},
];

export interface ReportOrderValues {
  /** Проект, по которому собираем (в API — company). Идентификатор из списка. */
  companyId: string;
  /** Границы наблюдения, ДД.ММ.ГГГГ. */
  periodFrom: string;
  periodTo: string;
  audience: ReportAudience;
  /**
   * Этажи, попадающие в документ. Обязательные — «Главное» и «Оценка» —
   * приходят выбранными и не снимаются: отчёт без них не отчёт.
   */
  sections: SectionId[];
  /**
   * SLA на закрытие критичного инцидента, часы. По нему считается, что
   * попало в «закрыто в срок», а что — задним числом.
   */
  slaHours: number;
  /**
   * Порог показа риска, рубли. Всё, что дешевле, в документ не идёт: список
   * из сорока замечаний по двести рублей прячет одно на восемьдесят тысяч.
   */
  riskThreshold: number;
  /** Кому уходит готовый отчёт. Пусто — отчёт просто ляжет в хранилище. */
  recipientEmail: string;
  /** Написать получателю, когда сборка закончится. */
  notifyOnReady: boolean;
}

/** Значения по умолчанию — то, с чего специалист начинает в девяти случаях из десяти. */
export const DEFAULT_ORDER_VALUES: ReportOrderValues = {
  companyId: COMPANY_OPTIONS[0].id,
  periodFrom: '01.06.2026',
  periodTo: '30.06.2026',
  audience: 'advertiser',
  sections: [
    'summary',
    'score',
    'risks',
    'campaigns',
    'clients',
    'team',
    'incidents',
    'questions',
    'tasks',
    'control',
    'notifications',
  ],
  slaHours: 24,
  riskThreshold: 5_000,
  recipientEmail: '',
  notifyOnReady: true,
};

/** Этажи, которые нельзя выключить. */
export const REQUIRED_SECTIONS: SectionId[] = ['summary', 'score'];
